import React, { useState, useEffect } from "react";
import { Mail, MailOpen, Video, Check, X, Clock } from "lucide-react";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";

interface Notification {
  id?: string;
  trigger: string;
  message: string;
  triggeredBy: string;
  createdAt: any;
  read: boolean;
  userId: string;
  userEmail: string;
  meta?: any;
  joined?: boolean;
}

interface NotificationCardProps {
  notification: Notification;
  onMarkAsRead: (notificationId: string) => void;
  onSupportRequestAction?: (notification: Notification, action: string) => void;
  onFundRequestAction?: (notification: Notification, action: string) => void;
  user?: any;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onMarkAsRead,
  onSupportRequestAction,
  onFundRequestAction,
  user,
}) => {
  const [hasJoined, setHasJoined] = useState(notification.joined || false);
  const [isExpired, setIsExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isFundRequestPending, setIsFundRequestPending] = useState(true);

  console.log(notification, "NotificationCard");
  // Check if support request is expired and calculate time left
  useEffect(() => {
    if (
      notification.trigger === "new_support_request" &&
      !notification.read &&
      (notification.meta?.creation_timestamp ||
        notification.meta?.expiration_time)
    ) {
      // Get the expiration or creation time from the notification metadata
      let expirationTime: number;

      if (notification.meta.expiration_time) {
        // If the expiration time is explicitly provided
        expirationTime = notification.meta.expiration_time;
      } else if (notification.meta.creation_timestamp) {
        // Calculate the expiration time (60 seconds after creation)
        expirationTime = notification.meta.creation_timestamp + 60000; // 60 seconds in milliseconds
      } else {
        // Fallback to using the notification creation time
        const createdTime = new Date(notification.createdAt).getTime();
        expirationTime = createdTime + 60000; // 60 seconds in milliseconds
      }

      // Declare timer variable at the scope level before it's used
      let timer: NodeJS.Timeout;

      // Setup timer to update countdown
      const updateTimeLeft = () => {
        const now = Date.now();
        const remaining = Math.max(
          0,
          Math.floor((expirationTime - now) / 1000)
        );

        setTimeLeft(remaining);

        // If time has expired, mark as expired
        if (remaining <= 0) {
          setIsExpired(true);
          clearInterval(timer);

          // If this notification hasn't been handled yet, mark it as missed
          if (!notification.read && onSupportRequestAction) {
            onSupportRequestAction(notification, "missed");
          }
        }
      };

      // Initial calculation
      updateTimeLeft();

      // Update every second
      timer = setInterval(updateTimeLeft, 1000);

      return () => clearInterval(timer);
    }
  }, [notification, onSupportRequestAction]);

  // Format timestamp to "05/30/2025, 01:01:32 PM" format
  const formatTime = (timestamp: any) => {
    if (!timestamp) return "Just now";

    const createdTime = new Date(timestamp);

    // Format as MM/DD/YYYY, HH:MM:SS AM/PM
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    return createdTime.toLocaleString("en-US", options);
  };

  // Handle click without triggering the parent onClick
  const handleActionClick = (e: React.MouseEvent, action: string) => {
    e.stopPropagation();

    if (action === "accept" || action === "reject" || action === "missed") {
      if (notification.trigger === "new_support_request" && onSupportRequestAction) {
        onSupportRequestAction(notification, action);
      } else if (
        (notification.trigger === "fund_request_receive" ||
          notification.trigger === "fund_request" ||
          notification.trigger === "fund_request_details") &&
        onFundRequestAction
      ) {
        onFundRequestAction(notification, action);
      }
    } else if (
      action === "join" &&
      notification.meta?.roomId &&
      !hasJoined &&
      !notification.read
    ) {
      // Set joined status to true
      setHasJoined(true);

      // Join video call
      const roomId = notification.meta.roomId;
      const customerId = notification.meta.customerId || notification.userId;
      const expertId = notification.meta.acceptedBy || notification.triggeredBy;

      // Determine role based on the current user
      // If the current user is the expert, role is 'expert', otherwise 'customer'
      const isExpert = user?.id === expertId || user?.uid === expertId;
      const role = isExpert ? "expert" : "customer";

      const rtcUrl = `${import.meta.env.VITE_SUPPORTY_RTC}/id=${roomId}/name=${user?.name || (role === "expert" ? "Expert" : "Customer")}/email=${user?.email || ""}/expertId=${expertId}/customerId=${customerId}/role=${role}`;

      window.open(rtcUrl, "_blank");
    }
  };

  // Get first letter of triggeredBy for avatar
  const avatarInitial = notification.trigger
    ? notification.trigger.charAt(0).toUpperCase()
    : "N";

  // Check if this is a missed request notification
  const isMissedRequest =
    notification.trigger === "support_request_missed" ||
    (notification.trigger === "new_support_request" && isExpired);

  // Determine if this notification should show action buttons
  const showSupportActions =
    notification.trigger === "new_support_request" &&
    !notification.read &&
    !isExpired && // Don't show accept/reject actions if expired
    onSupportRequestAction;

  // Determine if we should show the "Mark as Missed" button for expired requests
  const showMarkAsMissed =
    notification.trigger === "new_support_request" &&
    isExpired &&
    !notification.read &&
    onSupportRequestAction;

  const showFundActions =
    (notification.trigger === "fund_request_receive" ||
      notification.trigger === "fund_request" ||
      notification.trigger === "fund_request_details") &&
    !notification.read &&
    notification.meta?.requestId &&
    onFundRequestAction &&
    isFundRequestPending; // <-- Only show if pending

  // Only show join call button for accepted requests with a roomId
  const showJoinCall =
    notification.trigger === "support_request_accepted" &&
    notification.meta?.roomId &&
    !notification.meta?.rejected;

  // Check fund request status in Firestore for fund request notifications (real-time)
 
  return (
    <div
      className={`flex flex-col ${
        notification.read ? "bg-white" : "bg-[#EBEBEB]"
      } p-3 px-5 rounded-[8px] border-b border-[#D9D9D9] ${
        !showSupportActions && !showFundActions && !showMarkAsMissed
          ? "cursor-pointer"
          : ""
      }`}
      onClick={() => {
        // Only mark as read on click for unread notifications
        if (
          notification.id &&
          !notification.read &&
          !showSupportActions &&
          !showFundActions &&
          !showMarkAsMissed
        ) {
          onMarkAsRead(notification.id);
        }
      }}
    >
      <div className="flex items-center justify-between">
        {/* Left side - Avatar and content */}
        <div className="flex items-center space-x-3">
          {/* Avatar */}
          <div className="w-[64px] h-[64px] bg-gray-300 rounded-full flex items-center justify-center">
            <span className="text-gray-500 text-sm font-medium">
              {avatarInitial}
            </span>
          </div>

          {/* Content */}
          <div className="flex flex-col">
            <h3
              className={`text-[24px] nunito-semibold  ${
                notification.read ? " text-[#696969]" : " text-[#919191]"
              }`}
            >
              {/* Format trigger: remove underscores, capitalize words */}
              {notification.trigger
                ? notification.trigger
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())
                : ""}
              {/* {isExpired && notification.trigger === "new_support_request" && (
                <span className="ml-2 text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full">
                  Expired
                </span>
              )} */}
              {notification.trigger === "support_request_missed" && (
                <span className="ml-2 text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full inline-flex items-center">
                  <X className="w-3 h-3 mr-1" /> Missed
                </span>
              )}
              {/* Show countdown timer for active support requests */}
              {notification.trigger === "new_support_request" &&
                !notification.read &&
                !isExpired &&
                timeLeft > 0 && (
                  <span className="ml-2 text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full inline-flex items-center">
                    <Clock className="w-3 h-3 mr-1" /> {timeLeft}s
                  </span>
                )}
            </h3>
            <p>{notification?.meta?.customerEmail}</p>
            <p
              className={`text-[20px] nunito-semibold    ${
                notification.read ? " text-[#919191]" : " text-[#404040]"
              }`}
            >
              {notification.message}
            </p>
            <span
              className={`text-[14px] nunito-semibold  mt-1 ${
                notification.read ? " text-[#919191]" : " text-[#696969]"
              }`}
            >
              {formatTime(notification.createdAt)}
            </span>
          </div>
        </div>
        {/* Right side - Mail icon and action buttons */}
        <div className="flex items-center gap-3">
          {/* Action buttons moved to the left of mail icon */}
          {(showSupportActions || showMarkAsMissed || showFundActions) && (
            <div className="flex gap-2">
              {/* Support/Fund request accept/reject buttons */}
              {(showSupportActions || showFundActions) && (
                <>
                  <button
                    className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center"
                    onClick={(e) => handleActionClick(e, "accept")}
                  >
                    <Check className="w-4 h-4 mr-1" /> Accept
                  </button>
                  <button
                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center"
                    onClick={(e) => handleActionClick(e, "reject")}
                  >
                    <X className="w-4 h-4 mr-1" /> Reject
                  </button>
                </>
              )}
            </div>
          )}

          {/* Mail icon */}
          <div>
            {notification.read ? (
              <MailOpen className="w-[32px] h-[32px] text-[#919191]" />
            ) : (
              <Mail className="w-[32px] h-[32px] text-[#919191]" />
            )}
          </div>
        </div>
      </div>
      {/* Additional details for requests */}
      {notification.trigger === "support_request_missed" &&
        !notification.meta?.isExpertNotification && (
          <p className="text-xs text-gray-600 font-medium bg-gray-100 px-2 py-1 rounded mt-2">
            The expert was unable to respond in time
            {notification.meta?.rejectReason &&
              `: ${notification.meta.rejectReason}`}
          </p>
        )}
      {/* Show expert-specific notification for missed requests */}
      {notification.trigger === "support_request_missed" &&
        notification.meta?.isExpertNotification && (
          <div className="text-xs text-red-600 font-medium bg-red-50 px-3 py-2 rounded mt-2 border border-red-200">
           
            <p className="mt-1">
              This request missed because you didn't respond within the
              60-second time limit.
            </p>
          </div>
        )}
      {notification.trigger === "new_support_request" &&
         (
          <div className="text-xs text-black font-medium bg-white px-3 py-2 rounded mt-2 border border-gray-200">
            <p className="font-bold"> support request</p>
            <p className="mt-1">
              Customer: {notification.meta?.customerName || "Unknown"}
            </p>
            {notification.meta?.question && (
              <p className="mt-1">Question: {notification.meta.question}</p>
            )}
          
          </div>
        )}
      {notification.trigger === "support_request_submitted" &&
         (
          <div className="text-xs text-black font-medium bg-white px-3 py-2 rounded mt-2 border border-gray-200">
            <p className="font-bold"> you send request</p>
            <p className="mt-1">
              Expert: {notification.meta?.expertName || "Unknown"}
            </p>
            {notification.meta?.question && (
              <p className="mt-1">Question: {notification.meta.question}</p>
            )}
          
          </div>
        )}
      
    </div>
  );
};

export default NotificationCard;
